import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2, AlertCircle, FileImage, Clock, Download, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pv`;

export default function PrintRequest() {
  const { requestId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/print/${requestId}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError("Richiesta non trovata");
        } else {
          setError("Errore nel caricamento della richiesta");
        }
        return;
      }
      
      const data = await res.json();
      setRequest(data);
      
      // If there are attachments, fetch their URLs
      if (data.attachments_count > 0) {
        fetchAttachments();
      }
    } catch (err) {
      setError("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    try {
      setLoadingAttachments(true);
      const res = await fetch(`${API}/attachments/${requestId}`);
      if (res.ok) {
        const data = await res.json();
        setAttachments(data.attachments || []);
      }
    } catch (err) {
      console.error("Error fetching attachments:", err);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-xl font-bold text-gray-800 mb-2">Errore</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="print-request-page">
      {/* Print Button - Hidden on print */}
      <div className="print:hidden sticky top-0 bg-white border-b border-gray-200 p-4 shadow-sm z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-800">Richiesta Preventivo</h1>
          <Button
            onClick={handlePrint}
            className="bg-primary hover:bg-primary/90 text-white h-12 px-6 text-lg font-bold"
            data-testid="print-btn"
          >
            <Printer className="w-5 h-5 mr-2" />
            STAMPA
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="max-w-2xl mx-auto p-6 print:p-8 print:max-w-none">
        {/* Header */}
        <div className="text-center mb-8 print:mb-10">
          <h1 className="text-2xl font-bold text-gray-900 print:text-3xl">
            RICHIESTA PREVENTIVO
          </h1>
          <p className="text-gray-500 mt-1 print:text-gray-600">
            #{request.request_number}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="space-y-6 print:space-y-8">
          {/* Workshop */}
          <div className="border-b border-gray-200 pb-4 print:pb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">
              Officina
            </h2>
            <p className="text-lg font-semibold text-gray-900">
              {request.workshop_name}
            </p>
          </div>

          {/* Reference */}
          <div className="border-b border-gray-200 pb-4 print:pb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">
              Riferimento
            </h2>
            <p className="text-lg font-semibold text-gray-900">
              {request.request_number}
            </p>
          </div>

          {/* Plate */}
          <div className="border-b border-gray-200 pb-4 print:pb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">
              Targa
            </h2>
            <p className="text-2xl font-bold font-mono text-gray-900 tracking-wider">
              {request.plate}
            </p>
          </div>

          {/* Parts Requested */}
          <div className="border-b border-gray-200 pb-4 print:pb-6">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Ricambi richiesti ({request.selected_items?.length || 0})
            </h2>
            <ul className="space-y-2">
              {request.selected_items_labels?.map((label, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-900">
                  <span className="text-primary font-bold">•</span>
                  <span>
                    {request.selected_items[idx] === "altro" && request.other_description
                      ? `Altro: ${request.other_description}`
                      : label}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Urgency */}
          {request.urgent && (
            <div className="border-b border-gray-200 pb-4 print:pb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 print:bg-yellow-100">
                <p className="text-yellow-800 font-bold text-lg">
                  ⚡ URGENTE OGGI
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          {request.notes && (
            <div className="border-b border-gray-200 pb-4 print:pb-6">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                Note
              </h2>
              <p className="text-gray-900 whitespace-pre-wrap">
                {request.notes}
              </p>
            </div>
          )}

          {/* Attachments with download links */}
          {(request.attachments_count > 0 || attachments.length > 0) && (
            <div className="border-b border-gray-200 pb-4 print:pb-6">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
                Allegati ({request.attachments_count || attachments.length})
              </h2>
              
              {loadingAttachments ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Caricamento allegati...</span>
                </div>
              ) : attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((att, idx) => (
                    <div key={att.attachment_id || idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg print:bg-gray-100">
                      <FileImage className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">
                          {att.kind === "plate_photo" ? "Foto targa" : `Allegato ${idx + 1}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {att.mimetype} • {(att.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      {att.url ? (
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 print:hidden"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Apri
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400 print:hidden">
                          {att.error || "Non disponibile"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-600">
                  <FileImage className="w-5 h-5" />
                  <span>
                    {request.attachments_count} allegat{request.attachments_count > 1 ? "i" : "o"}
                  </span>
                </div>
              )}
              
              {/* Print-only note about attachments */}
              {attachments.length > 0 && (
                <p className="hidden print:block mt-3 text-sm text-gray-500 italic">
                  Gli allegati sono disponibili online: {window.location.href}
                </p>
              )}
            </div>
          )}

          {/* Timestamp */}
          <div className="pt-4 print:pt-6">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Clock className="w-4 h-4" />
              <span>Inviata il {formatDate(request.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Footer - Print only */}
        <div className="hidden print:block mt-16 pt-8 border-t border-gray-300 text-center text-gray-500 text-sm">
          <p>Documento generato da Richiesta Rapida Ricambi</p>
          <p className="mt-1">{window.location.origin}/print/{requestId}</p>
        </div>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 2cm;
          }
          
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
}
